<template>
    <div>
        <!-- Loading Spinner (affiché à la place du tableau) -->
        <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-12">
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-12 ppv2-w-12 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-3 ppv2-block ppv2-text-sm ppv2-text-gray-600">{{ $t('invoices.loading_invoices') }}</span>
            </div>
        </div>

        <!-- DataTable (affiché quand loading est false) -->
        <data-table
            v-else
            ref="dataTable"
            :items="invoices"
            :columns="computedColumns"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :perPage="perPage"
            :total="total"
            :enableMultiSelect="false"
            :enableDragDrop="false"
            @page-changed="handlePageChange"
        >
            <!-- Custom cell: Customer - Lien bleu cliquable (comme original) -->
            <template #column-customer="{ item }">
                <a
                    v-if="item.customer?.id && !isWalkInCustomerByName(item.customer_name)"
                    href="#"
                    @click.prevent="openCustomerModal(item.customer.id)"
                    class="ppv2-text-[#62C2F6] hover:ppv2-underline ppv2-m-1"
                >
                    {{ item.customer_name }}
                </a>
                <span v-else class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: normalizeFontSize(13) }">{{
                    $t('invoices.walk_in_customer')
                }}</span>
            </template>

            <!-- Custom cell: Status - Badge moderne avec couleur de fond -->
            <template #column-status="{ item }">
                <span
                    v-if="item.status"
                    :class="getStatusBadgeClass(item.status.type)"
                    class="ppv2-inline-flex ppv2-items-center ppv2-px-3 ppv2-py-1 ppv2-rounded-md ppv2-text-sm poppinsMedium"
                >
                    {{ getStatusLabel(item.status.type, item.status.percentage) }}
                </span>
                <span v-else class="ppv2-text-gray-400 ppv2-text-xs">-</span>
            </template>

            <!-- Custom cell: Articles - Liste formatée (comme original) -->
            <template #column-articles="{ item }">
                <ul
                    v-if="item.articles_display && item.articles_display.length > 0"
                    class="ppv2-mb-0 ppv2-pl-0 ppv2-list-none ppv2-text-xs"
                >
                    <li v-for="(article, index) in item.articles_display" :key="index" class="ppv2-mb-1">
                        {{ article.name }}
                        <span>({{ article.type }})</span>
                    </li>
                </ul>
                <span v-else class="ppv2-text-gray-400 ppv2-text-xs"> {{ $t('invoices.deleted_order') }} </span>
            </template>

            <!-- Custom cell: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-gap-2">
                    <button
                        @click="viewInvoice(item.order_id)"
                        class="ppv2-p-2 ppv2-rounded-lg hover:ppv2-bg-gray-100 ppv2-transition-colors ppv2-text-[#1e3a8a]"
                        :title="$t('invoices.view_details')"
                    >
                        <Eye class="ppv2-w-5 ppv2-h-5" />
                    </button>
                    <button
                        @click="sendInvoice(item.order_id)"
                        class="ppv2-p-2 ppv2-rounded-lg hover:ppv2-bg-gray-100 ppv2-transition-colors ppv2-text-[#0ea5e9]"
                        :title="$t('invoices.send_by_email')"
                    >
                        <Send class="ppv2-w-5 ppv2-h-5" />
                    </button>
                    <button
                        @click="downloadPDF(item.order_id)"
                        class="ppv2-p-2 ppv2-rounded-lg hover:ppv2-bg-gray-100 ppv2-transition-colors ppv2-text-[#ef4444]"
                        :title="$t('invoices.generate_pdf')"
                    >
                        <FileDown class="ppv2-w-5 ppv2-h-5" />
                    </button>
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import DataTable from '../Shared/DataTable.vue'
import { eventBus } from '../Customer/eventBus'
import Search from '../V3/Experience/components/Search.vue'
import ExportSelect from '../Shared/ExportSelect.vue'
import { Eye, Send, FileDown } from 'lucide-vue'
import { getPerPage, setPerPage } from '../../utils/perPage'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'InvoiceDataTable',
    components: {
        DataTable,
        Search,
        ExportSelect,
        Eye,
        Send,
        FileDown,
    },
    props: {
        startDate: {
            type: String,
            required: true,
        },
        endDate: {
            type: String,
            required: true,
        },
        selectedColumns: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
        paymentStatus: {
            type: String,
            default: 'all',
        },
    },
    data() {
        return {
            // API Management
            invoices: [],
            loading: false,
            currentPage: 1,
            totalPages: 1,
            perPage: getPerPage('invoices_items_per_page', 10),
            total: 0,
            searchQuery: '',
            exportOptions: [
                { value: 'csv', label: 'CSV' },
                { value: 'xls', label: 'Excel' },
            ],

            // Columns configuration
            columns: [
                {
                    key: 'invoice_number',
                    label: this.$t('invoices.columns.invoice_number'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'order_number',
                    label: this.$t('invoices.columns.order_number'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'customer',
                    label: this.$t('invoices.columns.customer'),
                    sortable: true,
                    slot: 'cell-customer',
                    type: 'text',
                },
                {
                    key: 'date_ouverture',
                    label: this.$t('invoices.columns.open_date'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'date_fermeture',
                    label: this.$t('invoices.columns.close_date'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'status',
                    label: this.$t('invoices.columns.status'),
                    sortable: true,
                    slot: 'cell-status',
                    type: 'badge',
                },
                {
                    key: 'total_products',
                    label: this.$t('invoices.columns.total_products'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'total_prestations',
                    label: this.$t('invoices.columns.total_prestations'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'total_series',
                    label: this.$t('invoices.columns.total_series'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'base_price_formatted',
                    label: this.$t('invoices.columns.base_price'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'paid_price_formatted',
                    label: this.$t('invoices.columns.paid_price'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'total_packages',
                    label: this.$t('invoices.columns.total_packages'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'total_voucher',
                    label: this.$t('invoices.columns.total_vouchers'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'total_promo',
                    label: this.$t('invoices.columns.total_promo'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'articles',
                    label: this.$t('invoices.columns.articles'),
                    sortable: false,
                    slot: 'cell-articles',
                    type: 'text',
                },
                {
                    key: 'refund_amount_formatted',
                    label: this.$t('invoices.columns.refund_amount'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'email',
                    label: this.$t('invoices.columns.email'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'phone',
                    label: this.$t('invoices.columns.phone'),
                    sortable: false,
                    type: 'number',
                },
                {
                    key: 'payment_method',
                    label: this.$t('invoices.columns.payment_method'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'voucher_promo_used',
                    label: this.$t('invoices.columns.voucher_promo_used'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'actions',
                    label: this.$t('common.actions'),
                    sortable: false,
                    slot: 'cell-actions',
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        computedColumns() {
            // Si aucune colonne sélectionnée, utiliser toutes les colonnes
            if (!this.selectedColumns || this.selectedColumns.length === 0) {
                return this.allColumns
            }

            // Créer un Set des colonnes sélectionnées pour recherche rapide
            const selectedKeys = new Set()
            this.selectedColumns.forEach((selectedCol) => {
                // Ajouter la clé directe
                selectedKeys.add(selectedCol.data)
                selectedKeys.add(selectedCol.name)

                // Ajouter les correspondances spéciales
                if (selectedCol.data === 'serial_number') selectedKeys.add('invoice_number')
                if (selectedCol.data === 'order_id') selectedKeys.add('order_number')
                if (selectedCol.data === 'created_at') selectedKeys.add('date_ouverture')
                if (selectedCol.data === 'paid_at') selectedKeys.add('date_fermeture')
                if (selectedCol.data === 'base_price') selectedKeys.add('base_price_formatted')
                if (selectedCol.data === 'paid_price') selectedKeys.add('paid_price_formatted')
                if (selectedCol.data === 'total_subscriptions') selectedKeys.add('total_series')
                if (selectedCol.data === 'refund_amount') selectedKeys.add('refund_amount_formatted')
                if (selectedCol.data === 'phone_number') selectedKeys.add('phone')
                if (selectedCol.data === 'amount_reduction_method_used') selectedKeys.add('voucher_promo_used')
            })

            // Filtrer allColumns en gardant l'ordre original
            // Toujours inclure la colonne "actions" en dernier
            const filteredColumns = this.allColumns.filter((col) => {
                return col.key === 'actions' || selectedKeys.has(col.key)
            })

            console.log('📊 Colonnes calculées:', {
                total: filteredColumns.length,
                keys: filteredColumns.map((c) => c.key),
            })

            return filteredColumns
        },
        allColumns() {
            // Toutes les colonnes possibles (anciennement dans data.columns)
            return this.columns
        },
    },
    watch: {
        startDate() {
            console.log('📅 Date de début changée:', this.startDate)
            this.currentPage = 1 // Reset à la page 1
            this.fetchInvoices()
        },
        endDate() {
            console.log('📅 Date de fin changée:', this.endDate)
            this.currentPage = 1 // Reset à la page 1
            this.fetchInvoices()
        },
        filters: {
            handler() {
                console.log('🔍 Filtres changés:', this.filters)
                this.currentPage = 1 // Reset à la page 1
                this.fetchInvoices()
            },
            deep: true,
        },
        paymentStatus() {
            console.log('🏷️ Statut de paiement changé:', this.paymentStatus)
            this.currentPage = 1
            this.fetchInvoices()
        },
    },
    mounted() {
        console.log('🚀 InvoiceDataTable monté - Chargement initial...')
        // Charger les données initiales
        this.fetchInvoices()

        // Écouter les changements de perPage depuis Pagination
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    beforeDestroy() {
        // Nettoyer les listeners
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        isWalkInCustomerByName(name) {
            if (!name) return true
            const trimmed = name.trim()
            return !trimmed || trimmed === '- -' || trimmed === '-' || trimmed === '- -'
        },
        async fetchInvoices() {
            this.loading = true
            try {
                const token = document.head.querySelector('meta[name="csrf-token"]')
                const response = await fetch('/back-office/invoices-v2/list', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token ? token.content : '',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        startDate: this.startDate,
                        endDate: this.endDate,
                        page: this.currentPage,
                        perPage: this.perPage,
                        search: this.searchQuery,
                        filters: this.filters || {},
                        paymentStatus: this.paymentStatus,
                    }),
                })

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const data = await response.json()

                // Mettre à jour les données (Laravel Resource format)
                this.invoices = data.data || []
                this.currentPage = parseInt(data.meta?.current_page) || 1
                this.totalPages = parseInt(data.meta?.last_page) || 1
                this.total = parseInt(data.meta?.total) || 0

                console.log('✅ Factures chargées:', {
                    count: this.invoices.length,
                    page: this.currentPage,
                    totalPages: this.totalPages,
                    total: this.total,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.invoices = []
            } finally {
                this.loading = false
            }
        },
        handlePageChange(page) {
            console.log('📄 Changement de page:', page)
            this.currentPage = page
            this.fetchInvoices()
        },
        handlePerPageChange(newPerPage) {
            console.log('📊 Changement de perPage:', newPerPage)
            this.perPage = parseInt(newPerPage)
            setPerPage('invoices_items_per_page', this.perPage)
            this.currentPage = 1 // Reset à la page 1
            this.fetchInvoices()
        },
        reload() {
            // Méthode publique pour recharger depuis le parent
            console.log('🔄 Rechargement manuel du DataTable')
            this.currentPage = 1
            this.fetchInvoices()
        },
        getStatusBadgeClass(statusType) {
            // Classes modernes pour les badges de statut
            const badgeClasses = {
                full: 'ppv2-bg-green-500 ppv2-text-white', // Payé - Vert
                partial: 'ppv2-bg-yellow-400 ppv2-text-gray-800', // Partiel - Jaune
                none: 'ppv2-bg-red-500 ppv2-text-white', // Non payé - Rouge
            }
            return badgeClasses[statusType] || 'ppv2-bg-gray-400 ppv2-text-white'
        },
        getStatusLabel(statusType, percentage) {
            // Labels pour les statuts
            const labels = {
                full: this.$t('invoices.status_labels.paid'),
                partial: percentage
                    ? this.$t('invoices.status_labels.partial_percent', { percentage })
                    : this.$t('invoices.status_labels.partial'),
                none: this.$t('invoices.status_labels.due'),
            }
            return labels[statusType] || this.$t('invoices.status_labels.unknown')
        },
        getStatusClass(statusName) {
            const statusClasses = {
                // Statuts de paiement
                paid: 'ppv2-bg-green-100 ppv2-text-green-800',
                pending: 'ppv2-bg-yellow-100 ppv2-text-yellow-800',
                cancelled: 'ppv2-bg-red-100 ppv2-text-red-800',
                refunded: 'ppv2-bg-gray-100 ppv2-text-gray-800',
                partial: 'ppv2-bg-blue-100 ppv2-text-blue-800',
                // Statuts de commande
                completed: 'ppv2-bg-green-100 ppv2-text-green-800',
                processing: 'ppv2-bg-blue-100 ppv2-text-blue-800',
                failed: 'ppv2-bg-red-100 ppv2-text-red-800',
                'on-hold': 'ppv2-bg-orange-100 ppv2-text-orange-800',
            }
            return statusClasses[statusName] || 'ppv2-bg-gray-100 ppv2-text-gray-800'
        },
        openCustomerModal(userId) {
            // Ouvrir la fiche client dans une iframe (comme dans la version originale)
            // Utiliser la fonction globale si elle existe (pour compatibilité avec l'ancien code)
            if (typeof window.setOrderToModal === 'function') {
                window.setOrderToModal(userId)
            } else {
                // Sinon, ouvrir directement dans un nouvel onglet
                window.open(`/back-office/customer/${userId}/edit`, '_blank')
            }
        },
        viewInvoice(orderId) {
            console.log('📄 Voir la facture:', orderId)
            window.location.href = `/back-office/commandes/${orderId}/descriptif`
        },
        sendInvoice(orderId) {
            if (confirm(this.$t('invoices.confirm_send_email'))) {
                console.log('📄 Envoyer la facture:', orderId)
                window.location.href = `/back-office/invoice/${orderId}/send`
            }
        },
        downloadPDF(orderId) {
            console.log('📄 Télécharger la facture:', orderId)
            window.open(`/back-office/invoice/${orderId}`, '_blank')
        },
        handleSearchUpdate(searchValue) {
            console.log('🔍 Recherche:', searchValue)
            this.searchQuery = searchValue
            this.$emit('search', searchValue)
            this.currentPage = 1
            this.fetchInvoices()
        },
        async exportData(format) {
            console.log(`📤 Export ${format} demandé`)
            console.log('🔍 Données actuelles:', {
                startDate: this.startDate,
                endDate: this.endDate,
                searchQuery: this.searchQuery,
                filters: this.filters,
                selectedColumns: this.selectedColumns,
            })
            this.loading = true
            try {
                // Construire l'URL avec tous les paramètres actuels
                const params = new URLSearchParams({
                    startDate: this.startDate,
                    endDate: this.endDate,
                    search: this.searchQuery,
                    format: format,
                    paymentStatus: this.paymentStatus,
                    // Ajouter les filtres actuels
                    ...this.filters,
                })

                // Ajouter les colonnes sélectionnées si nécessaire
                if (this.selectedColumns && this.selectedColumns.length > 0) {
                    this.selectedColumns.forEach((col) => {
                        params.append('columns[]', col.data || col.key)
                    })
                }

                const response = await fetch(`/back-office/invoices-v2/export?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        // Ne pas mettre Content-Type: application/json pour un GET qui retourne un fichier
                    },
                })

                if (!response.ok) {
                    throw new Error(`${this.$t('invoices.export_error_msg')} ${response.status}`)
                }

                // Gérer le téléchargement du fichier
                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `${this.$t('invoices.export_filename_prefix')}_${this.startDate}_${this.endDate}.${
                    format === 'xls' ? 'xlsx' : 'csv'
                }`
                document.body.appendChild(a)
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        handleRowAction(action) {
            console.log('Row action:', action)
        },
    },
}
</script>

<style scoped>
/* Styles spécifiques au DataTable des factures */
</style>
